import React, { useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Container,
  Flex,
  Heading,
  HStack,
  Icon,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
  Button,
} from '@chakra-ui/react';
import { FiSearch, FiPlay, FiLock, FiRefreshCw, FiWifi, FiWifiOff, FiStar } from 'react-icons/fi';
import { GiRollingDices } from 'react-icons/gi';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CordeCommonModelsDtoMachineMachine } from '@/api/generated/v1/api';
import { useMachineStatusStream } from '../../hooks/useMachineStatusStream';
import { useFavorites } from '../../hooks/useFavorites';

// Deterministic vivid gradient per machine so the lobby feels like a game gallery.
const GRADIENTS = [
  'linear(135deg, #7928CA, #FF0080)',
  'linear(135deg, #2193b0, #6dd5ed)',
  'linear(135deg, #f7971e, #ffd200)',
  'linear(135deg, #11998e, #38ef7d)',
  'linear(135deg, #ee0979, #ff6a00)',
  'linear(135deg, #396afc, #2948ff)',
  'linear(135deg, #c31432, #240b36)',
  'linear(135deg, #16a085, #f4d03f)',
];

const gradientFor = (seed: string): string => {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return GRADIENTS[hash % GRADIENTS.length];
};

const normStatus = (status?: string): string => (status || '').toUpperCase();

// Statuses a player is allowed to see. Offline / disabled machines are hidden.
const isVisibleToPlayer = (status?: string): boolean => {
  const s = normStatus(status);
  return s === 'FREE' || s === 'BUSY' || s === 'AVAILABLE' || s === 'OCCUPIED';
};

const isFree = (status?: string): boolean => {
  const s = normStatus(status);
  return s === 'FREE' || s === 'AVAILABLE';
};

export const PlayLobbyPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [favoritesOnly, setFavoritesOnly] = useState(false);

  const { isFavorite, toggleFavorite } = useFavorites();

  const { machines, status: wsStatus, reconnect } = useMachineStatusStream({
    autoReconnect: true,
    maxReconnectAttempts: 5,
    reconnectDelay: 3000,
  });

  const visibleMachines = useMemo(
    () => machines.filter((m) => isVisibleToPlayer(m.status)),
    [machines],
  );

  const categories = useMemo(
    () => Array.from(new Set(visibleMachines.map((m) => m.category).filter(Boolean))),
    [visibleMachines],
  );

  const filteredMachines = useMemo(() => {
    return visibleMachines.filter((machine) => {
      const matchesSearch =
        !searchQuery ||
        machine.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        machine.category?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = !categoryFilter || machine.category === categoryFilter;
      const matchesFavorite = !favoritesOnly || isFavorite(machine.id);
      return matchesSearch && matchesCategory && matchesFavorite;
    });
  }, [visibleMachines, searchQuery, categoryFilter, favoritesOnly, isFavorite]);

  const handleOpenMachine = (machine: CordeCommonModelsDtoMachineMachine) => {
    if (!isFree(machine.status)) return;
    navigate(`/play/${machine.id}`);
  };

  if (!wsStatus.connected && machines.length === 0) {
    return (
      <Container maxW="container.xl" py={8}>
        <Flex justify="center" align="center" h="300px" direction="column" gap={4}>
          <Spinner size="xl" thickness="4px" color="purple.400" />
          <Text color="text.secondary">{t('play.connecting')}</Text>
          {wsStatus.error && (
            <Text color="red.400" fontSize="sm">
              {wsStatus.error}
            </Text>
          )}
        </Flex>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={6}>
      <Box
        position="relative"
        borderRadius="3xl"
        p={{ base: 4, md: 8 }}
        color="white"
        overflow="hidden"
        bgGradient="radial(120% 90% at 50% -10%, #3a1d63 0%, #1a0f33 45%, #0b0618 100%)"
        boxShadow="0 24px 70px rgba(0,0,0,0.55)"
        border="1px solid"
        borderColor="whiteAlpha.200"
      >
        {/* top marquee light */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          h="3px"
          bgGradient="linear(to-r, transparent, #ffd166, #ff4d8d, #8a5bff, transparent)"
          opacity={0.9}
        />

        <VStack spacing={{ base: 5, md: 7 }} align="stretch" position="relative">
          {/* Hero */}
          <Flex justify="space-between" align={{ base: 'start', md: 'center' }} wrap="wrap" gap={4}>
            <HStack spacing={4} align="center">
              <Icon
                as={GiRollingDices}
                boxSize={{ base: 10, md: 14 }}
                color="yellow.300"
                filter="drop-shadow(0 0 16px rgba(245,197,24,0.5))"
              />
              <VStack align="start" spacing={1}>
                <Heading
                  size={{ base: 'lg', md: 'xl' }}
                  fontWeight="extrabold"
                  bgGradient="linear(to-r, #ffd166, #ff8fb1, #b98bff)"
                  bgClip="text"
                  textShadow="0 0 24px rgba(185,139,255,0.35)"
                >
                  {t('play.title')}
                </Heading>
                <Text fontSize={{ base: 'sm', md: 'md' }} color="whiteAlpha.800">
                  {t('play.subtitle')}
                </Text>
              </VStack>
            </HStack>

            <HStack spacing={3}>
              <HStack
                spacing={2}
                px={3}
                py={1.5}
                borderRadius="full"
                bg="blackAlpha.400"
                border="1px solid"
                borderColor={wsStatus.connected ? 'green.400' : 'red.400'}
              >
                <Icon as={wsStatus.connected ? FiWifi : FiWifiOff} color={wsStatus.connected ? 'green.300' : 'red.300'} />
                <Text fontSize="xs" fontWeight="bold" letterSpacing="0.08em">
                  {wsStatus.connected ? t('play.live') : t('play.offline')}
                </Text>
              </HStack>
              <Text fontSize="sm" color="whiteAlpha.700">
                {t('play.machinesCount', { count: filteredMachines.length })}
              </Text>
              {!wsStatus.connected && (
                <Button leftIcon={<FiRefreshCw />} size="xs" variant="outline" colorScheme="whiteAlpha" onClick={reconnect}>
                  {t('play.reconnect')}
                </Button>
              )}
            </HStack>
          </Flex>

          {/* Filters */}
          <HStack spacing={3} wrap="wrap">
            <Flex
              align="center"
              bg="whiteAlpha.100"
              borderRadius="lg"
              px={3}
              maxW="360px"
              flex="1"
              minW="220px"
              borderWidth="1px"
              borderColor="whiteAlpha.200"
            >
              <Icon as={FiSearch} color="whiteAlpha.600" />
              <Input
                variant="unstyled"
                px={2}
                py={2}
                color="white"
                _placeholder={{ color: 'whiteAlpha.500' }}
                placeholder={t('play.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Flex>
            {categories.length > 0 && (
              <Select
                placeholder={t('play.allCategories')}
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                maxW="220px"
                bg="whiteAlpha.100"
                color="white"
                borderColor="whiteAlpha.200"
                sx={{ '& option': { color: '#000' } }}
              >
                {categories.map((category) => (
                  <option key={category} value={category as string}>
                    {category}
                  </option>
                ))}
              </Select>
            )}
            <Button
              leftIcon={<FiStar fill={favoritesOnly ? 'currentColor' : 'none'} />}
              variant={favoritesOnly ? 'solid' : 'outline'}
              colorScheme="yellow"
              onClick={() => setFavoritesOnly((v) => !v)}
            >
              {t('play.favoritesOnly')}
            </Button>
          </HStack>

          {/* Machine gallery */}
          {filteredMachines.length === 0 ? (
            <Flex
              justify="center"
              align="center"
              direction="column"
              gap={3}
              py={16}
              bg="whiteAlpha.50"
              borderRadius="2xl"
              border="1px dashed"
              borderColor="whiteAlpha.200"
            >
              <Icon as={favoritesOnly ? FiStar : GiRollingDices} boxSize={12} color="whiteAlpha.500" />
              <Text color="whiteAlpha.700" textAlign="center" px={6}>
                {favoritesOnly
                  ? t('play.noFavorites')
                  : visibleMachines.length === 0
                    ? t('play.noMachines')
                    : t('play.noMatch')}
              </Text>
            </Flex>
          ) : (
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 3, xl: 4 }} spacing={6}>
              {filteredMachines.map((machine) => {
                const free = isFree(machine.status);
                const fav = isFavorite(machine.id);
                return (
                  <Box
                    key={machine.id}
                    bg="whiteAlpha.100"
                    borderRadius="2xl"
                    overflow="hidden"
                    border="1px solid"
                    borderColor="whiteAlpha.200"
                    cursor={free ? 'pointer' : 'not-allowed'}
                    role="group"
                    transition="all 0.2s"
                    _hover={
                      free
                        ? { transform: 'translateY(-4px)', boxShadow: '0 16px 40px rgba(138,43,226,0.35)', borderColor: 'purple.400' }
                        : undefined
                    }
                    onClick={() => handleOpenMachine(machine)}
                    aria-label={machine.name || 'machine'}
                  >
                    {/* Cover */}
                    <Box
                      h="150px"
                      bgGradient={gradientFor(machine.id || machine.name || 'x')}
                      position="relative"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      filter={free ? 'none' : 'grayscale(0.7)'}
                      opacity={free ? 1 : 0.7}
                    >
                      <Icon as={GiRollingDices} boxSize={16} color="whiteAlpha.900" filter="drop-shadow(0 2px 8px rgba(0,0,0,0.5))" />
                      <Box
                        as="button"
                        aria-label={fav ? t('play.removeFavorite') : t('play.addFavorite')}
                        position="absolute"
                        top={2}
                        left={2}
                        w={9}
                        h={9}
                        borderRadius="full"
                        bg="blackAlpha.500"
                        color={fav ? 'yellow.300' : 'whiteAlpha.900'}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        _hover={{ bg: 'blackAlpha.700', transform: 'scale(1.1)' }}
                        transition="all 0.15s"
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          toggleFavorite(machine.id);
                        }}
                      >
                        <FiStar fill={fav ? 'currentColor' : 'none'} />
                      </Box>
                      <Badge
                        position="absolute"
                        top={3}
                        right={3}
                        colorScheme={free ? 'green' : 'orange'}
                        variant="solid"
                        borderRadius="full"
                        px={3}
                        py={1}
                        display="flex"
                        alignItems="center"
                        gap={1}
                      >
                        {free ? <FiPlay size={12} /> : <FiLock size={12} />}
                        {free ? t('play.available') : t('play.busy')}
                      </Badge>
                    </Box>

                    <Box p={5}>
                      <VStack align="start" spacing={2}>
                        <Heading size="md" noOfLines={1} color="white">
                          {machine.name || t('play.unnamed')}
                        </Heading>
                        {machine.category && (
                          <Badge colorScheme="purple" variant="subtle" borderRadius="md">
                            {machine.category}
                          </Badge>
                        )}
                        <Button
                          mt={2}
                          w="full"
                          colorScheme={free ? 'green' : 'gray'}
                          leftIcon={free ? <FiPlay /> : <FiLock />}
                          isDisabled={!free}
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            handleOpenMachine(machine);
                          }}
                        >
                          {free ? t('play.playNow') : t('play.busy')}
                        </Button>
                      </VStack>
                    </Box>
                  </Box>
                );
              })}
            </SimpleGrid>
          )}
        </VStack>
      </Box>
    </Container>
  );
};
